import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { Link as RouterLink, Navigate, useParams } from 'react-router-dom';
import type { IssueRun } from '../../data/types';
import {
  applicationPayment,
  certificatePrefix,
  closeExclusion,
  nextCertificateSequence,
  paidFraction,
} from '../../domain/raise/raiseCalc';
import { applicationReadiness } from '../../domain/kyc/readiness';
import { wholesaleModeFor } from '../../domain/kyc/wholesale';
import { DEMO_TODAY, money, nowStamp, units as formatUnits } from '../../lib/format';
import { newId } from '../../lib/random';
import { useRegistry } from '../../store/RegistryStore';
import { useTenantScope } from '../../store/selectors';
import DataTable from '../../ui/DataTable';
import { useToast } from '../../ui/feedback';
import { AboutThisPage, PageHeader, SectionCard, StatCard, StatGrid } from '../../ui/primitives';
import IssueRunSummary from './IssueRunSummary';

export default function FinancialClosePage() {
  const { offerId } = useParams();
  const scope = useTenantScope();
  const { state, dispatch } = useRegistry();
  const toast = useToast();
  const offer = scope.offers.find((item) => item.id === offerId);
  const unitClass = offer ? scope.classById.get(offer.classId) : undefined;
  const fund = offer ? scope.fundById.get(offer.fundId) : undefined;
  const [settlementDate, setSettlementDate] = useState(DEMO_TODAY);
  const [release, setRelease] = useState<IssueRun['certificateRelease']>('MANUAL');
  const [closeOfferAtIssue, setCloseOfferAtIssue] = useState(true);
  const [unitEdits, setUnitEdits] = useState<Record<string, string>>({});

  const issuePrice = unitClass?.issuePrice ?? 1;
  const paidPerUnit = Math.round(issuePrice * paidFraction(unitClass) * 10_000) / 10_000;
  const candidates = useMemo(
    () =>
      scope.applications
        .filter((application) => application.classId === offer?.classId)
        .map((application) => {
          const payment = applicationPayment(application, unitClass);
          const readiness = applicationReadiness(
            application,
            scope.holderByEntityId.get(application.investmentEntityId),
            scope.kycCases,
            DEMO_TODAY,
            settlementDate,
            wholesaleModeFor(state, application.fundId),
          );
          return {
            application,
            payment,
            reason: closeExclusion(application, payment, readiness),
          };
        })
        .filter((row) => !['ISSUED', 'REJECTED', 'WITHDRAWN'].includes(row.application.status)),
    [
      scope.applications,
      scope.holderByEntityId,
      scope.kycCases,
      offer,
      unitClass,
      settlementDate,
      state,
    ],
  );

  if (!offer || !unitClass || !fund) return <Navigate to="/manager/offers" replace />;
  if (!offer.issueAtClose) {
    return (
      <Alert
        severity="info"
        action={
          <Button component={RouterLink} to={`/manager/offers/${offer.id}`} size="small">
            Back to offer
          </Button>
        }
      >
        {fund.code} issues units as each application completes, so it has no financial close.
      </Alert>
    );
  }
  const activeRun = scope.issueRuns.find(
    (run) => run.offerId === offer.id && run.status !== 'REJECTED',
  );
  const rejected = scope.issueRuns.find(
    (run) => run.offerId === offer.id && run.status === 'REJECTED',
  );

  const included = candidates.filter((row) => !row.reason);
  const excluded = candidates.filter((row) => row.reason);
  const prefix = certificatePrefix(state, unitClass.id);
  const firstSequence = nextCertificateSequence(state, unitClass.id);
  const lines = included.map((row) => {
    const amountPaid = row.payment.received;
    const defaultUnits = Math.floor(amountPaid / paidPerUnit);
    const edited = unitEdits[row.application.id];
    return {
      applicationId: row.application.id,
      applicantName: row.application.applicantName,
      holderId: scope.holderByEntityId.get(row.application.investmentEntityId)?.id,
      amountPaid,
      units: edited !== undefined ? Number(edited) || 0 : defaultUnits,
      defaultUnits,
    };
  });
  const totalPaid = lines.reduce((sum, line) => sum + line.amountPaid, 0);
  const totalUnits = lines.reduce((sum, line) => sum + line.units, 0);

  const submit = () => {
    const run: IssueRun = {
      id: newId('ir'),
      tenantId: offer.tenantId,
      fundId: fund.id,
      classId: unitClass.id,
      offerId: offer.id,
      reference: `FC-${fund.code}-${scope.issueRuns.filter((item) => item.fundId === fund.id).length + 1}`,
      settlementDate,
      issuePrice,
      paidPerUnit,
      closeOffer: closeOfferAtIssue,
      certificateRelease: release,
      status: 'DRAFT',
      lines: lines.map(({ defaultUnits: _unused, ...line }) => line),
      createdBy: scope.user.name,
      createdAt: nowStamp(),
    };
    dispatch({ type: 'ISSUE_RUN_SAVE', run });
    dispatch({ type: 'ISSUE_RUN_SUBMIT', id: run.id });
    toast('Unit issue sent to Approvals');
  };

  return (
    <>
      <PageHeader
        eyebrow={
          <RouterLink to={`/manager/offers/${offer.id}`} style={{ color: 'inherit' }}>
            {offer.title}
          </RouterLink>
        }
        title="Financial close"
        subtitle="Issue units to every paid applicant in one batch, once the fund holds the asset."
      />
      <AboutThisPage>
        <p>
          The whole raise is issued together: the settlement date, price and units are checked once,
          a second person approves, and unit certificates are numbered and generated straight away.
        </p>
      </AboutThisPage>
      {activeRun ? (
        <IssueRunSummary run={activeRun} />
      ) : (
        <>
          {rejected && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {rejected.reference} was rejected by the approver. Check the details and submit again.
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
              }}
            >
              <TextField
                label="Settlement date (financial close)"
                type="date"
                value={settlementDate}
                onChange={(event) => setSettlementDate(event.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Amount paid per unit"
                value={`$${paidPerUnit.toFixed(4)}`}
                disabled
                helperText={
                  paidPerUnit < issuePrice
                    ? `Partly paid: $${issuePrice.toFixed(2)} issue price`
                    : 'Fully paid units'
                }
              />
              <TextField
                select
                label="Unit certificates"
                value={release}
                onChange={(event) =>
                  setRelease(event.target.value as IssueRun['certificateRelease'])
                }
              >
                <MenuItem value="MANUAL">Generate, release by hand</MenuItem>
                <MenuItem value="AUTO">Release to investors on issue</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={closeOfferAtIssue}
                    onChange={(event) => setCloseOfferAtIssue(event.target.checked)}
                  />
                }
                label="Close the offer on issue"
              />
            </Box>
          </Paper>
          <StatGrid min={170}>
            <StatCard label="Ready to issue" value={lines.length} tone="success" />
            <StatCard label="Investment amount" value={money(totalPaid)} />
            <StatCard label="Units" value={formatUnits(totalUnits)} />
            <StatCard
              label="Not included"
              value={excluded.length}
              tone={excluded.length ? 'warning' : 'neutral'}
            />
          </StatGrid>
          <SectionCard
            title="Ready to issue"
            subtitle={`Certificates will run from ${prefix}-${String(firstSequence).padStart(6, '0')}`}
            sx={{ mt: 2 }}
            noPadding
          >
            <DataTable
              dense
              rows={lines}
              rowKey={(row) => row.applicationId}
              emptyMessage="No paid applications yet"
              columns={[
                { key: 'name', label: 'Investor', render: (row) => row.applicantName },
                {
                  key: 'paid',
                  label: 'Investment amount',
                  align: 'right',
                  render: (row) => money(row.amountPaid, true),
                },
                {
                  key: 'units',
                  label: 'Units',
                  align: 'right',
                  render: (row) => (
                    <TextField
                      size="small"
                      value={unitEdits[row.applicationId] ?? String(row.defaultUnits)}
                      onChange={(event) =>
                        setUnitEdits((current) => ({
                          ...current,
                          [row.applicationId]: event.target.value.replace(/[^\d]/g, ''),
                        }))
                      }
                      inputProps={{
                        'aria-label': `Units for ${row.applicantName}`,
                        style: { textAlign: 'right' },
                      }}
                      sx={{ width: 130 }}
                    />
                  ),
                },
              ]}
            />
          </SectionCard>
          {excluded.length > 0 && (
            <SectionCard
              title="Not included"
              subtitle="These stay open for a later issue"
              sx={{ mt: 2 }}
            >
              {excluded.map((row) => (
                <Typography key={row.application.id} variant="body2" sx={{ mb: 0.75 }}>
                  <RouterLink to={`/manager/applications/${row.application.id}`}>
                    {row.application.applicantName}
                  </RouterLink>{' '}
                  · {row.reason}
                  {row.payment.outstanding > 0 && row.payment.received > 0
                    ? ` (${money(row.payment.outstanding)} outstanding)`
                    : ''}
                </Typography>
              ))}
            </SectionCard>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              disabled={lines.length === 0 || lines.some((line) => line.units <= 0)}
              onClick={submit}
            >
              Submit unit issue for approval
            </Button>
          </Box>
        </>
      )}
    </>
  );
}
